import React from 'react';
import { problemQuestions, strongQuestions, mockAggregateStats, mockQuestionStats } from '../../data/carouselMockData';

const ProblemAreasSlide: React.FC = () => {
  return (
    <div className="flex flex-col items-center justify-center h-full p-8">
      <div className="max-w-5xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Identify Problem Areas
          </h2>
          <p className="text-xl text-gray-600 mb-6">
            Actionable insights to guide your teaching
          </p>
        </div>

        {/* Class Coherence Card */}
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl shadow-2xl p-8 mb-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
              <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
            </svg>
            <h3 className="text-2xl font-bold">Class Coherence Analysis</h3>
          </div>
          <p className="text-lg mb-4">
            {mockAggregateStats.std_deviation < 15 ? (
              <>
                Standard deviation of <strong>{mockAggregateStats.std_deviation.toFixed(1)}%</strong> indicates a{' '}
                <strong className="text-yellow-300">highly coherent</strong> class with similar understanding levels.
              </>
            ) : mockAggregateStats.std_deviation < 25 ? (
              <>
                Standard deviation of <strong>{mockAggregateStats.std_deviation.toFixed(1)}%</strong> shows{' '}
                <strong className="text-yellow-300">moderate variation</strong> in understanding across students.
              </>
            ) : (
              <>
                Standard deviation of <strong>{mockAggregateStats.std_deviation.toFixed(1)}%</strong> reveals{' '}
                <strong className="text-yellow-300">significant variation</strong> - consider differentiated instruction.
              </>
            )}
          </p>
          <div className="bg-white/20 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">
                {mockAggregateStats.std_deviation < 20
                  ? "Most students are progressing together - great for collaborative learning!"
                  : "Some students may benefit from additional support or advanced challenges."}
              </span>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Topics Needing Review */}
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl shadow-lg border-2 border-red-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <h3 className="text-lg font-bold text-red-800">Topics to Review</h3>
            </div>
            {problemQuestions.length > 0 ? (
              <ul className="space-y-3">
                {problemQuestions.map((q, idx) => {
                  const questionIndex = parseInt(q.id.substring(1)) - 1;
                  const stat = mockQuestionStats[questionIndex];
                  return (
                    <li
                      key={q.id}
                      className="bg-white rounded-lg p-3 shadow-sm"
                      style={{
                        animation: 'slideUp 0.4s ease-out',
                        animationDelay: `${idx * 100}ms`,
                        animationFillMode: 'both'
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold shrink-0">
                          Q{questionIndex + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 mb-1">{q.question}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-600">
                            <span className="font-semibold text-red-600">
                              {stat.correct_percentage.toFixed(0)}% correct
                            </span>
                            <span>Avg: {stat.average_time_seconds.toFixed(1)}s</span>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="text-center py-8 text-gray-600">
                <div className="text-4xl mb-2">🎉</div>
                <p className="font-medium">No problem areas detected!</p>
                <p className="text-sm">All questions were well understood.</p>
              </div>
            )}
          </div>

          {/* Strong Areas */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-lg border-2 border-green-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <h3 className="text-lg font-bold text-green-800">Strong Areas</h3>
            </div>
            {strongQuestions.length > 0 ? (
              <ul className="space-y-3">
                {strongQuestions.slice(0, 3).map((q, idx) => {
                  const questionIndex = parseInt(q.id.substring(1)) - 1;
                  const stat = mockQuestionStats[questionIndex];
                  return (
                    <li
                      key={q.id}
                      className="bg-white rounded-lg p-3 shadow-sm"
                      style={{
                        animation: 'slideUp 0.4s ease-out',
                        animationDelay: `${idx * 100 + 200}ms`,
                        animationFillMode: 'both'
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold shrink-0">
                          Q{questionIndex + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 mb-1">{q.question}</p>
                          <div className="flex items-center gap-3 text-xs text-gray-600">
                            <span className="font-semibold text-green-600">
                              {stat.correct_percentage.toFixed(0)}% correct
                            </span>
                            <span>Well mastered!</span>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="text-center py-8 text-gray-600">
                <p className="font-medium">No strong areas yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ProblemAreasSlide;
